import React, { useEffect, useCallback, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";
import girl_photo from "../assets/girlHero.webp";

const Stars = () => {
  const createStars = useCallback(() => {
    const container = document.querySelector(".star-container");
    if (container) {
      // Limpiar estrellas existentes
      container.innerHTML = "";

      // Crear 150 estrellas
      for (let i = 0; i < 150; i++) {
        const star = document.createElement("span");
        const size = Math.random() * 6; // Tamaño aleatorio entre 0 y 3px
        const duration = 20 + Math.random() * 10; // Duración entre 20 y 30 segundos
        const delay = Math.random() * -20; // Retraso negativo para que algunas estrellas ya estén en movimiento
        const angle = Math.random() * 360; // Ángulo aleatorio entre 0 y 360 grados

        star.style.cssText = `
          position: absolute;
          top: 50%;
          left: 50%;
          width: ${size}px;
          height: ${size}px;
          background: rgba(255, 255, 255, ${Math.random()});
          border-radius: 50%;
          animation: moveOut ${duration}s linear infinite;
          animation-delay: ${delay}s;
          --angle: ${angle}deg;
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
        `;
        container.appendChild(star);
      }
    }
  }, []);

  useEffect(() => {
    createStars();
    window.addEventListener("resize", createStars);
    return () => window.removeEventListener("resize", createStars);
  }, [createStars]);

  return <div className="star-container absolute inset-0 z-0" />;
};

const Lasers = () => {
  useEffect(() => {
    const createLaser = () => {
      const container = document.querySelector(".laser-container");
      if (container) {
        const laser = document.createElement("div");
        const screenWidth = window.innerWidth;
        const duration = 4 + Math.random() * 3; // Duración entre 4 y 7 segundos
        const angle = Math.random() * 360; // Ángulo aleatorio
        const hue = Math.random() * 360; // Color aleatorio en HSL
        const color = `hsl(${hue}, 100%, 60%)`;

        // Calcular tamaños responsivos
        const baseWidth = screenWidth * 0.15; // 15% del ancho de la pantalla
        const baseHeight = Math.max(2, screenWidth * 0.002); // Grosor mínimo 2px, escalado
        const blurRadius = Math.max(1, screenWidth * 0.001); // Desenfoque responsivo
        const shadowSpread = Math.max(5, screenWidth * 0.01); // Difusión de sombra responsiva
        const translateDistance = screenWidth * 1.2; // Distancia de animación (120% del ancho)

        laser.className = "laser-beam";
        laser.style.cssText = `
          position: absolute;
          top: 50%;
          left: 50%;
          width: ${baseWidth}px;
          height: ${baseHeight}px;
          background: linear-gradient(to right, ${color}00, ${color}ff 50%, ${color}00);
          filter: blur(${blurRadius}px) brightness(1.5);
          animation: laserMove ${duration}s linear forwards, laserFlicker 0.1s ease-in-out infinite;
          --angle: ${angle}deg;
          --translate-distance: ${translateDistance}px;
          transform-origin: center;
          transform: translate(-50%, -50%) rotate(var(--angle)) scale(0.1, 0.1);
          box-shadow: 
            0 0 ${shadowSpread}px ${color}, 
            0 0 ${shadowSpread * 2}px ${color}, 
            0 0 ${shadowSpread * 3}px ${color};
        `;

        // Crear partículas responsivas
        const particleCount =
          screenWidth < 768 ? 2 : 3 + Math.floor(Math.random() * 3); // Menos partículas en móviles
        for (let i = 0; i < particleCount; i++) {
          const particle = document.createElement("div");
          const particleDelay = Math.random() * duration;
          const particleSize = Math.max(2, screenWidth * 0.0025); // Tamaño de partícula responsivo
          particle.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            width: ${particleSize}px;
            height: ${particleSize}px;
            background: ${color};
            border-radius: 50%;
            animation: laserParticle ${duration}s linear forwards;
            animation-delay: ${particleDelay}s;
            transform: translate(-50%, -50%) rotate(${angle}deg) translateX(0);
            filter: blur(${blurRadius}px);
            box-shadow: 0 0 ${shadowSpread * 0.5}px ${color};
          `;
          laser.appendChild(particle);
        }

        // Eliminar el láser al finalizar la animación
        laser.addEventListener("animationend", () => {
          laser.remove();
        });

        container.appendChild(laser);
      }
    };

    // Ajustar intervalo según tamaño de pantalla (más lento en móviles)
    const intervalTime = window.innerWidth < 768 ? 200 : 150;
    const interval = setInterval(createLaser, intervalTime);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="laser-container absolute inset-0 z-[1] pointer-events-none" />
  );
};
const Hero = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setVisible(true);
    }, 100); // pequeño delay para permitir animación

    return () => clearTimeout(timeout);
  }, []);
  return (
    <>
      <Helmet>
        <title>Laser Tag Cornwall – Laser Zone Arena</title>
        <meta
          name="description"
          content="Disfruta la mejor experiencia de Laser Tag en Cornwall, ON. Reserva ahora en Laser Zone Arena, abierto Mié–Dom 2 pm–10 pm."
        />
        <link
          rel="canonical"
          href="https://lazerzonearena.ca/laser-tag-cornwall"
        />
      </Helmet>
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#141318] to-[#1F2937] pt-[env(safe-area-inset-top)]">
      <h1 className="sr-only">Laser Tag Cornwall – Laser Zone Arena</h1>
      {/* Efecto de estrellas */}
      <Lasers />
      <Stars />

      {/* Contenido principal con efecto de enfoque */}
      <div
        className={`relative z-10 text-center px-4 w-full transform-gpu transition-all duration-1000 ease-out
  ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        <div className="relative inline-block space-y-8">
          {/* Texto con efecto de movimiento */}
          <div className="relative z-10 inline-block">
            {/* Capa de borde */}
            <div className="audiowide-font absolute inset-0 z-10 text-[clamp(2rem,8vw,5rem)] opacity-60 -m-2 md:whitespace-nowrap">
              <span className="text-transparent text-stroke-white-sm md:text-stroke-white-md lg:text-stroke-white-lg">
                LAZER ZONE ARENA
              </span>
            </div>
            <img
              src={girl_photo}
              alt="Laser Zone Arena"
              className=" sm:block absolute top-[160%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[2] w-[80%] md:w-[65%] lg:w-[55%] max-w-[950px] min-w-[120px] h-auto object-contain"
            />

            {/* Capa principal */}
            <h1 className="audiowide-font relative z-0 text-[clamp(2rem,8vw,5rem)] md:whitespace-nowrap">
              <span className="text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]">
                LAZER ZONE ARENA
              </span>
            </h1>
          </div>

          {/* Resto del contenido con aparición escalonada */}
          <div className="relative z-20 animate-content-appear flex flex-col items-center gap-4">
            <p className="text-[clamp(1rem,3vw,1.5rem)] text-white font-inter leading-relaxed bg-black/50 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-xl shadow-[0_0_30px_rgba(0,0,0,0.5)]">
              The Ultimate Laser Tag Experience Awaits!
            </p>

            <Link
              to="/pricing"
              className="bg-[#FFD404] no-underline hover:bg-[#ffde3a] text-[#012727] px-6 md:px-10 py-3 md:py-5 rounded-full text-base md:text-lg font-bold transition-all transform hover:scale-105 shadow-[0_0_25px_rgba(255,212,4,0.4)]"
            >
              BOOK NOW
            </Link>
          </div>

          {/* Key Features */}
          <div className="relative z-20 flex flex-wrap justify-center gap-5 mt-8 font-inter">
            <div className="flex items-center bg-[#ffffff08] backdrop-blur-sm px-6 py-3 rounded-full border border-[#FFD404]/20">
              <span className="text-[#FFD404] mr-3">•</span>
              <span className="text-white text-[clamp(0.875rem,2vw,1.125rem)]">
                Live score tracking
              </span>
            </div>
            <div className="flex items-center bg-[#ffffff08] backdrop-blur-sm px-6 py-3 rounded-full border border-[#FFD404]/20">
              <span className="text-[#FFD404] mr-3">•</span>
              <span className="text-white text-[clamp(0.875rem,2vw,1.125rem)]">
                Immersive battle arenas
              </span>
            </div>
            <div className="flex items-center bg-[#ffffff08] backdrop-blur-sm px-6 py-3 rounded-full border border-[#FFD404]/20">
              <span className="text-[#FFD404] mr-3">•</span>
              <span className="text-white text-[clamp(0.875rem,2vw,1.125rem)]">
                High-performance tag gear
              </span>
            </div>
          </div>
        </div>
      </div>

      <style jsx="true" global="true">{`
        @keyframes moveStar {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(200%);
          }
        }

        @keyframes laserMove {
          0% {
            transform: translate(-50%, -50%) rotate(var(--angle))
              scale(0.1, 0.1);
            opacity: 0.5;
          }
          50% {
            transform: translate(-50%, -50%) rotate(var(--angle))
              translateX(50vw) scale(1, 1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) rotate(var(--angle))
              translateX(100vw) scale(1, 1);
            opacity: 0;
          }
        }
      `}</style>
    </section>
    </>
  );
};
  

export default Hero;
